import { conditionManager } from "./ConditionManager.js";
import type { ApplyOptions } from "./ConditionManager.js";
import { EventBus } from "../EventBus.js";
import { pushFeed } from "../Feed.js";
import type { ConditionId } from "../types/entity.js";

/**
 * EV-E4 — the thin runtime layer that makes `conditionManager` load-
 * bearing: shared incapacitation/cooldown queries for CombatManager, and
 * a single apply helper so every source (enemy attack riders, traps,
 * spells) produces the same feed line + event.
 */

/** Conditions that block the holder's actions entirely (combat-physical). */
const INCAPACITATING: readonly ConditionId[] = [
  "sleep",
  "paralyzed",
  "stunned",
  "petrified",
  "unconscious",
  "incapacitated",
];

export function isIncapacitated(targetId: string): boolean {
  return INCAPACITATING.some((id) => conditionManager.has(targetId, id));
}

/** ATB cooldown multiplier: Haste ×0.5, Slow ×2 (combat-physical). */
export function conditionCooldownScale(targetId: string): number {
  let scale = 1;
  if (conditionManager.has(targetId, "haste")) scale *= 0.5;
  if (conditionManager.has(targetId, "slow")) scale *= 2;
  return scale;
}

/** Sleep ends on first damage (combat-physical: "first damage wakes"). */
export function wakeOnDamage(targetId: string): void {
  if (conditionManager.clear(targetId, "sleep")) {
    EventBus.emit("combat:condition_ended", { targetId, conditionId: "sleep" });
  }
}

/** Human-readable condition label for feed lines. */
function conditionLabel(id: ConditionId): string {
  return id.charAt(0).toUpperCase() + id.slice(1);
}

/**
 * Apply a condition with the standard feed line + event. Returns whether
 * state changed (per the stack policy).
 */
export function applyConditionTo(
  targetId: string,
  targetName: string,
  conditionId: ConditionId,
  opts: ApplyOptions = {},
): boolean {
  const changed = conditionManager.apply(targetId, conditionId, opts);
  if (changed) {
    EventBus.emit("combat:condition_applied", { targetId, conditionId });
    pushFeed(`${targetName} is ${conditionLabel(conditionId)}!`);
  }
  return changed;
}
